import {
  HHButton,
  HHStack,
  HHTextField,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import Autocomplete from '@mui/material/Autocomplete';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { EsiFeeScheduleType } from '../../types';
import { esiFeeScheduleFilterSchema } from './validation-schema';

export interface EsiFeeScheduleFilterParams {
  programs: string[];
  paymentTypes: string[];
  priceMin: string;
  priceMax: string;
  upc: string;
}

export const defaultEsiFeeScheduleFilters: EsiFeeScheduleFilterParams = {
  programs: [],
  paymentTypes: [],
  priceMin: '',
  priceMax: '',
  upc: '',
};

const UPC_FIELD_SX = {
  flex: '2 1 180px',
  minWidth: 140,
} as const;

const AUTOCOMPLETE_SX = {
  flex: '1.5 1 160px',
  minWidth: 140,
} as const;

interface EsiFeeScheduleFilterBarProps {
  filterParams: EsiFeeScheduleFilterParams;
  setFilterParams: Dispatch<SetStateAction<EsiFeeScheduleFilterParams>>;
  rows: EsiFeeScheduleType[];
  onFiltersApplied: () => void;
}

export const EsiFeeScheduleFilterBar = ({
  filterParams,
  setFilterParams,
  rows,
  onFiltersApplied,
}: EsiFeeScheduleFilterBarProps): JSX.Element => {
  const programOptions = [
    ...new Set(rows.map(r => r.program).filter(Boolean) as string[]),
  ].sort((a, b) => a.localeCompare(b));

  const paymentTypeOptions = [...new Set(rows.map(r => r.paymentType))].sort(
    (a, b) => a.localeCompare(b),
  );

  const { handleSubmit, control, formState, reset, trigger } =
    useForm<EsiFeeScheduleFilterParams>({
      mode: 'onChange',
      defaultValues: filterParams,
      resolver: yupResolver(esiFeeScheduleFilterSchema),
      criteriaMode: 'all',
    });

  const { isDirty, isValid, errors } = formState;

  useEffect(() => {
    reset(filterParams);
    trigger().catch(() => undefined);
  }, [filterParams, reset, trigger]);

  const onApplyClick = (): void => {
    handleSubmit(data => {
      setFilterParams(data);
      onFiltersApplied();
      reset(data);
    })().catch(() => undefined);
  };

  const handleReset = (): void => {
    reset(defaultEsiFeeScheduleFilters);
    setFilterParams(defaultEsiFeeScheduleFilters);
    onFiltersApplied();
  };

  const isFiltersDefault =
    filterParams.programs.length === 0 &&
    filterParams.paymentTypes.length === 0 &&
    filterParams.priceMin === '' &&
    filterParams.priceMax === '' &&
    filterParams.upc === '';

  return (
    <HHStack
      direction="row"
      alignItems="flex-end"
      flexWrap="wrap"
      sx={{ gap: 2, flex: 1, minWidth: 0, paddingRight: 6 }}
    >
      <Controller
        control={control}
        name="upc"
        render={({ field: { onChange, value } }): JSX.Element => (
          <HHTextField
            hhVariant="variant-bypass"
            variant="standard"
            label="UPC"
            value={value}
            onChange={(e): void => onChange(e.target.value)}
            sx={UPC_FIELD_SX}
            size="small"
            data-testid="esi-fee-schedule-upc-input"
            inputProps={{ 'aria-label': 'UPC', name: 'upc' }}
          />
        )}
      />

      <Controller
        control={control}
        name="programs"
        render={({ field: { onChange, value } }): JSX.Element => (
          <Autocomplete
            multiple
            id="esi-program-filter"
            data-testid="esi-program-filter"
            value={value}
            options={programOptions}
            size="small"
            sx={AUTOCOMPLETE_SX}
            limitTags={1}
            onChange={(_, newValue): void => onChange(newValue)}
            getOptionLabel={(option): string => option}
            renderInput={(params): JSX.Element => (
              <HHTextField
                {...params}
                hhVariant="variant-bypass"
                variant="standard"
                label="Program"
              />
            )}
          />
        )}
      />

      <Controller
        control={control}
        name="paymentTypes"
        render={({ field: { onChange, value } }): JSX.Element => (
          <Autocomplete
            multiple
            id="esi-payment-type-filter"
            data-testid="esi-payment-type-filter"
            value={value}
            options={paymentTypeOptions}
            size="small"
            sx={AUTOCOMPLETE_SX}
            limitTags={1}
            onChange={(_, newValue): void => onChange(newValue)}
            getOptionLabel={(option): string => option}
            renderInput={(params): JSX.Element => (
              <HHTextField
                {...params}
                hhVariant="variant-bypass"
                variant="standard"
                label="Payment Type"
              />
            )}
          />
        )}
      />

      <HHStack direction="row" spacing={1} alignItems="flex-end">
        <Controller
          control={control}
          name="priceMin"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="standard"
              type="number"
              label="Price Min"
              value={value}
              onChange={(e): void => onChange(e.target.value)}
              error={!!errors.priceMin}
              helperText={errors.priceMin?.message ?? ''}
              sx={{ flex: '0 0 auto', minWidth: 80, maxWidth: 110 }}
              size="small"
              data-testid="esi-fee-schedule-price-min-input"
              inputProps={{ 'aria-label': 'Price Min', name: 'priceMin' }}
            />
          )}
        />
        <Controller
          control={control}
          name="priceMax"
          render={({ field: { onChange, value } }): JSX.Element => (
            <HHTextField
              hhVariant="variant-bypass"
              variant="standard"
              type="number"
              label="Price Max"
              value={value}
              onChange={(e): void => onChange(e.target.value)}
              error={!!errors.priceMax}
              helperText={errors.priceMax?.message ?? ''}
              sx={{ flex: '0 0 auto', minWidth: 80, maxWidth: 110 }}
              size="small"
              data-testid="esi-fee-schedule-price-max-input"
              inputProps={{ 'aria-label': 'Price Max', name: 'priceMax' }}
            />
          )}
        />
      </HHStack>

      <HHStack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ marginLeft: 'auto', flexShrink: 0 }}
      >
        <HHButton
          size="medium"
          hhVariant="variant-bypass"
          variant="contained"
          data-testid="esi-fee-schedule-apply-button"
          onClick={onApplyClick}
          disabled={!isDirty || !isValid}
        >
          Apply
        </HHButton>
        <HHButton
          size="medium"
          hhVariant="variant-bypass"
          variant="outlined"
          data-testid="esi-fee-schedule-reset-button"
          onClick={handleReset}
          disabled={isFiltersDefault && !isDirty}
        >
          Reset
        </HHButton>
      </HHStack>
    </HHStack>
  );
};
